import { useMemo, useState } from 'react';
import {
    validateBillingInfoForm,
    validateCustomerInfoForm
} from '../../Hooks/CustomerInfo/ValidateCustomerInfoPageHook';
import StatusInfoBox from '../../../../components/core/StatusInfoBox/StatusInfoBox';
import { useCart } from '../../../../contexts/CartContext/CartContext.hooks';
import CheckoutBox from '../../../../components/core/CheckoutBox/CheckoutBox';
import ViaeroButton from '../../../../components/core/ViaeroButton/ViaeroButton';
import SocialSecurityInput from '../../../../components/forms/inputs/SocialSecurityInput';
import useCreditCheckStyles from './CreditCheck.styles';
import CreditCheckAgreementInput from '../../../../components/forms/inputs/CreditCheckAgreementInput';
import CreditCheckFollowupBox from './CreditCheckFollowupBox/CreditCheckFollowupBox';
import CreateCustomerAndGetCreditCheck, {
    ICreateCustomerAndCreditCheckResponse
} from './CreditCheck.api';
import LoadingWheel from '../../../../components/core/LoadingWheel/LoadingWheel';
import { DuplicateCustomerCheck } from '../../../../api/Idi/Idi.api';
import { IMatchingCustomerResponse } from '../../../../api/Idi/Idi.api.types';
import DuplicateCustomerPopup from '../../../../components/popups/DuplicateCustomerPopup/DuplicateCustomerPopup';
import { Config } from 'config';
import ReactPixel from 'react-facebook-pixel';
import {
    SendCreditCheckFailureEmail,
    SendCreditCheckSuccessEmail
} from 'contexts/CartContext/CartContext.api';
import { getComplianzConsent } from 'utils/tracking';

export interface ICreditCheckProps {
    failureFollowUpTitle?: string;
    failureFollowUpMessage?: string;
}

const CreditCheck = (props: ICreditCheckProps) => {
    const { failureFollowUpTitle, failureFollowUpMessage } = props;

    const { cart, handleUpdateCart, updateAgreements, setIsDuplicateCustomer, updateCustomerInfo } =
        useCart();

    const styles = useCreditCheckStyles();

    const [statusMessage, setStatusMessage] = useState('');

    const [creditCheckAgreement, setCreditCheckAgreement] = useState(false);

    const [maskedSocialSecurity, setMaskedSocialSecurity] = useState('');

    const [socialSecurity, setSocialSecurity] = useState('');

    const [dataLoading, setDataLoading] = useState(false);

    const [showDuplicateCustomerPopup, setShowDuplicateCustomerPopup] = useState(false);

    const onCreditCheckClick = () => {
        const customerInfoValidation = validateCustomerInfoForm(cart);
        if (!customerInfoValidation.isValid) {
            setStatusMessage(customerInfoValidation.messages[0]);
            return;
        }

        const billingInfoValidation = validateBillingInfoForm(cart);
        if (!billingInfoValidation.isValid) {
            setStatusMessage(billingInfoValidation.messages[0]);
            return;
        }

        if (maskedSocialSecurity.length !== 11) {
            setStatusMessage('Please enter a valid social security number.');
            return;
        }

        if (!creditCheckAgreement) {
            setStatusMessage('Please agree to the credit check authorization.');
            return;
        }

        setStatusMessage('');
        setDataLoading(true);

        if (Config.checkDuplicateAccounts) {
            DuplicateCustomerCheck(socialSecurity, HandleDuplicateCustomerResponse, () => {
                console.log('Duplicate customer check failed');
            });
        } else {
            HandleCreditCheck();
        }
    };

    const HandleDuplicateCustomerResponse = (response: IMatchingCustomerResponse[]) => {
        if (response.length === 0) HandleCreditCheck();
        else HandleIsDuplicateCustomer();
    };

    const HandleIsDuplicateCustomer = () => {
        setIsDuplicateCustomer(true);
        setDataLoading(false);
        setShowDuplicateCustomerPopup(true);
    };

    const HandleCreditCheck = () => {
        CreateCustomerAndGetCreditCheck(
            cart.customerInfo,
            cart.alternateContact,
            socialSecurity,
            (response: ICreateCustomerAndCreditCheckResponse) => {
                // Get consent status
                const consent = getComplianzConsent();

                if (response.creditClass === 'A') {
                    const customerInfo = {
                        ...cart.customerInfo,
                        customerId: response.customerID
                    };

                    updateCustomerInfo(customerInfo);

                    const agreements = {
                        ...cart.agreements,
                        creditCheckInformation: {
                            creditChecked: true,
                            passedCreditCheck: true,
                            addedPhoneNumber: false
                        }
                    };

                    updateAgreements(agreements);

                    const updatedCart = {
                        ...cart,
                        customerInfo: { ...customerInfo },
                        agreements: { ...agreements }
                    };
                    SendCreditCheckSuccessEmail(updatedCart);
                    if (
                        consent.marketing === 'allow' &&
                        typeof ReactPixel.trackCustom === 'function'
                    ) {
                        ReactPixel.trackCustom('CreditCheck', { cart: updatedCart });
                    }
                    handleUpdateCart(updatedCart);
                } else {
                    const agreements = {
                        ...cart.agreements,
                        creditCheckInformation: {
                            creditChecked: true,
                            passedCreditCheck: false,
                            addedPhoneNumber: false
                        }
                    };
                    updateAgreements(agreements);
                    const updatedCart = {
                        ...cart,
                        agreements: { ...agreements }
                    };
                    SendCreditCheckFailureEmail(updatedCart);
                    handleUpdateCart(updatedCart);
                }

                setDataLoading(false);
            },
            () => {
                console.error('CreateCustomerAndGetCreditCheck() failed.');
                setDataLoading(false);

                const agreements = {
                    ...cart.agreements,
                    creditCheckInformation: {
                        creditChecked: true,
                        passedCreditCheck: false,
                        addedPhoneNumber: false
                    }
                };
                updateAgreements(agreements);
                SendCreditCheckFailureEmail({
                    ...cart,
                    agreements: { ...agreements }
                });
            }
        );
    };

    const statusInfoBox = useMemo(() => {
        return statusMessage ? <StatusInfoBox isSuccessful={false} text={statusMessage} /> : <></>;
    }, [statusMessage]);

    const creditCheckStatusBox = useMemo(() => {
        const creditCheckInfo = cart.agreements.creditCheckInformation;
        if (!creditCheckInfo.creditChecked && !creditCheckInfo.passedCreditCheck) return <></>;

        if (creditCheckInfo.creditChecked && creditCheckInfo.passedCreditCheck)
            return (
                <StatusInfoBox
                    isSuccessful
                    text={'Credit check complete.'}
                    className={styles.creditCheckStatusBox}
                />
            );

        if (creditCheckInfo.creditChecked && !creditCheckInfo.passedCreditCheck)
            return (
                <CreditCheckFollowupBox
                    followUpTitle={failureFollowUpTitle}
                    followUpMessage={failureFollowUpMessage}
                />
            );
    }, [cart.agreements, cart.agreements.creditCheckInformation, cart.customerInfo]);

    const creditCheckButton = useMemo(() => {
        return !cart.agreements.creditCheckInformation.creditChecked ? (
            <ViaeroButton
                className={styles.checkCreditButton}
                text={'Credit Check'}
                click={onCreditCheckClick}
                disabled={dataLoading}
            />
        ) : (
            <></>
        );
    }, [
        cart.agreements,
        cart.agreements.creditCheckInformation,
        maskedSocialSecurity,
        creditCheckAgreement,
        dataLoading
    ]);

    const duplicateCustomerPopup = useMemo(() => {
        return (
            <DuplicateCustomerPopup
                showPopup={showDuplicateCustomerPopup}
                setPopup={setShowDuplicateCustomerPopup}
            />
        );
    }, [showDuplicateCustomerPopup]);

    return (
        <CheckoutBox header={'Credit Check'}>
            <SocialSecurityInput
                maskedSocialSecurity={maskedSocialSecurity}
                setMaskedSocialSecurity={setMaskedSocialSecurity}
                socialSecurity={socialSecurity}
                setSocialSecurity={setSocialSecurity}
                disabled={dataLoading}
            />

            <p className={styles.billingText}>
                Please enter the social security number of the billable party
            </p>

            <CreditCheckAgreementInput
                checked={creditCheckAgreement}
                onChange={setCreditCheckAgreement}
                disabled={dataLoading}
            />

            {creditCheckStatusBox}

            {creditCheckButton}

            {statusInfoBox}

            {dataLoading && <LoadingWheel />}

            {duplicateCustomerPopup}
        </CheckoutBox>
    );
};

export default CreditCheck;
